import angular from 'angular';
import moment from 'moment';
import infoActionsTemplate from './infoActions.template.html';

export default angular
  .module('csm.books.com.infoActions', [
    'ui.bootstrap',
    'ui.grid',
    'ui.grid.edit',
    'ui.grid.autoResize',
    'ui.grid.resizeColumns',
    'ui.grid.cellNav'
  ])
  .component('infoActions', {
    bindings: {
      addressEntity: '<',
      allowReceivablesEdit: '<',
      barcode: '<',
      destinyStatus: '<',
      notifiers: '<',
      orgInfo: '<',
      onPay: '&',
      openOrders: '<',
      receivables: '<',
      showUnpaidItems: '<',
      viewOpenOrdersHandler: '&',
      viewReceivablesItemsHandler: '&',
      refreshDestinyFinesHandler: '&',
      refreshDestinyEntityFinesHandler: '&'
    },
    template: infoActionsTemplate,
    controller: infoActionsController
  })
  /**
   * Renders a UTC timestamp as a relative time ("3 hours ago", "in a day")
   * for the Destiny status line - used for both lastRefreshedUTC (past)
   * and nextUpdateUTC (future, since moment().fromNow() handles both).
   */
  .filter(
    'timeAgo',
    () => (value) => (value ? moment.utc(value).local().fromNow() : '')
  )
  /**
   * Renders a UTC timestamp in the browser's own local timezone/format,
   * shown alongside timeAgo in the same pill so a user sees both the exact
   * time and the at-a-glance elapsed time without doing the math themselves.
   */
  .filter(
    'localDateTime',
    () => (value) =>
      value ? moment.utc(value).local().format('MMM D, YYYY h:mm A') : ''
  ).name;

/*******************************************************************************
 * infoActionsController */
/**
 * Controller for the info actions component.
 *******************************************************************************/
/* @ngInject */ function infoActionsController($injector, $scope) {
  const $ctrl = this;
  const $svcs = { $ctrl };

  $ctrl.$onInit = function () {
    $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));

    /*-------------------------------------------------------------*/
    /* Initial grid setup. Use the bindings now that openOrders /  */
    /* receivables are passed in as `<` props rather than pulled   */
    /* from $ctrl.resolve. Defaults to [] so a missing input       */
    /* doesn't blow up grid initialization.                        */
    /*-------------------------------------------------------------*/
    $ctrl.setupOpenOrdersGrid();
    $ctrl.setupReceivablesGrid();
  };

  $scope.$watch(
    '$ctrl.dgReceivables.data',
    function (receivables) {
      if (!receivables) return;
      $ctrl.unpaidTotal = receivables.reduce(
        (prev, r) => (prev += r.outstanding - r.received),
        0
      );
    },
    true
  );

  $scope.$watch(
    '$ctrl.dgOpenOrders.data',
    function (openOrders) {
      if (!openOrders) return;
      $ctrl.pickupsTotal = openOrders.reduce(
        (prev, o) => (prev += o.qtyRemaining),
        0
      );
    },
    true
  );
}

/*******************************************************************************
 * initCtrl */
/**
 * Initializes the info actions controller properties and functions.
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs, SERVER) {
  Object.assign($ctrl, {
    // Properties
    dgOpenOrders: {
      data: []
    },
    dgReceivables: {
      data: []
    },
    disableOpenOrderSubmit: false,
    pickupsTotal: 0,
    unpaidTotal: 0,
    /*-------------------------------------------------------------*/
    /* Collapsed by default so the Destiny accordion is clearly    */
    /* set apart from the Unpaid Items grid rather than blending   */
    /* into it - a user opens it deliberately to check freshness   */
    /* or trigger a refresh, it isn't something they need open on  */
    /* every visit.                                                */
    /*-------------------------------------------------------------*/
    destinyCollapsed: true,
    // Injector Functions
    creditMemo: $injector.invoke(creditMemo, null, $svcs),
    isMakePaymentBtnDisabled: $injector.invoke(
      isMakePaymentBtnDisabled,
      null,
      $svcs
    ),
    isProcessOrdersBtnDisabled: $injector.invoke(
      isProcessOrdersBtnDisabled,
      null,
      $svcs
    ),
    paySelected: $injector.invoke(paySelected, null, $svcs),
    processOrders: $injector.invoke(processOrders, null, $svcs),
    refreshOpenOrders: $injector.invoke(refreshOpenOrders, null, $svcs),
    refreshReceivables: $injector.invoke(refreshReceivables, null, $svcs),
    refreshDestinyFines: $injector.invoke(refreshDestinyFines, null, $svcs),
    refreshDestinyEntityFines: $injector.invoke(
      refreshDestinyEntityFines,
      null,
      $svcs
    ),
    setupOpenOrdersGrid: $injector.invoke(setupOpenOrdersGrid, null, $svcs),
    setupReceivablesGrid: $injector.invoke(setupReceivablesGrid, null, $svcs),
    showDestiny: $injector.invoke(showDestiny, null, $svcs),
    typeLabelFunction: $injector.invoke(typeLabelFunction, null, $svcs)
  });
}

/*******************************************************************************
 * creditMemo */
/**
 * Opens the credit memo modal for the receivable. If a memo is created, reload
 * the receivable list.
 *
 * @param  receivable  Receivable item to credit.
 *******************************************************************************/
/* @ngInject */ function creditMemo(
  $ctrl,
  Alert,
  BusyScreen,
  CreditMemoDialog,
  Tools
) {
  return function (receivable) {
    CreditMemoDialog.show(receivable).then(() => {
      BusyScreen.setBusy(true);

      /*----------------------------------------------------------------*/
      /* refreshReceivables() takes no entity ID - it already reads     */
      /* whatever context (entity/barcode/org) the dialog was opened    */
      /* with. addressEntity is legitimately null here (e.g. a barcode  */
      /* or org-wide receivables lookup with no "From" entity chosen),  */
      /* so don't dereference it.                                      */
      /*----------------------------------------------------------------*/
      $ctrl
        .refreshReceivables()
        .then(() => {
          // Notify for the credit memo
          $ctrl.notifiers.creditMemo(receivable);
        })
        .catch((resp) => {
          Alert.error(
            `Failed to refresh the list of Unpaid Items. Error: ${Tools.serviceErrorHandler(resp)}`,
            'Unpaid Items'
          );
        })
        .finally(() => {
          BusyScreen.setBusy(false);
        });
    });
  };
}

/*******************************************************************************
 * isMakePaymentBtnDisabled */
/**
 * If no receivables are selected then we disable the make payment button.
 *******************************************************************************/
/* @ngInject */ function isMakePaymentBtnDisabled($ctrl) {
  return function () {
    return (
      $ctrl.dgReceivables.api &&
      $ctrl.dgReceivables.api.selection.getSelectedCount() === 0
    );
  };
}

/*******************************************************************************
 * isProcessOrdersBtnDisabled */
/**
 * Checks if any the open orders have had a fulfilled quantity > 0. If there are
 * no orders that have had a fulfilled quantity set then we disable the process
 * orders button.
 *******************************************************************************/
/* @ngInject */ function isProcessOrdersBtnDisabled($ctrl) {
  return function () {
    if (!$ctrl.dgOpenOrders || $ctrl.dgOpenOrders.data?.length === 0)
      return true;

    const fulfilledOrders = $ctrl.dgOpenOrders.data.filter(
      (order) => order.qtyFill > 0
    );

    return fulfilledOrders.length < 1 || $ctrl.disableOpenOrderSubmit;
  };
}

/*******************************************************************************
 * paySelected */
/**
 * Returns the selected receivables to the parent component (WNR or POS) so they
 * can be added to the receipt.
 *******************************************************************************/
/* @ngInject */ function paySelected($ctrl) {
  return function () {
    $ctrl.onPay({
      receivables: $ctrl.dgReceivables.api.selection.getSelectedRows()
    });
  };
}

/*******************************************************************************
 * processOrders */
/**
 * Finds all the open orders that have a delivery quantity > 0 and processes
 * them. If this function properly processes the open orders then it a receipt id
 * is returned and a new window is opened with newly created receipt. Then we
 * refresh the list of open orders for the selected entity or receipt barcode.
 *******************************************************************************/
/* @ngInject */ function processOrders(
  Alert,
  $ctrl,
  BooksSvc,
  BusyScreen,
  Tools
) {
  return function () {
    Alert.clear();

    /**
     * Disable the submit button to prevent processing multiple pickup receipts.
     **/
    $ctrl.disableOpenOrderSubmit = true;

    const fulfilledOrders = $ctrl.dgOpenOrders.data.filter(
      (order) => order.qtyFill > 0
    );

    if (fulfilledOrders.length < 1) {
      Alert.error(
        'There are no open orders have been selected to be fulfilled.',
        'Open Orders'
      );
      $ctrl.disableOpenOrderSubmit = false;
      return;
    }

    Alert.confirm('Do you want to process these Open Orders?', 'Open Orders')
      .then(() => {
        BusyScreen.setBusy(true);

        return BooksSvc.processOpenOrders(fulfilledOrders);
      })
      .then((resp) => {
        window.open(`PrintReceiptPDF.aspx?receiptID=${resp.data}`, '_blank');

        Alert.success(
          'Successfully created a pickup receipt for those open orders.',
          'Open Orders'
        );

        if (!!$ctrl.notifiers.pickup) $ctrl.notifiers.pickup();

        const barcode = $ctrl.barcode ? $ctrl.barcode : null;

        return $ctrl.refreshOpenOrders();
      })
      .catch((resp) => {
        BusyScreen.setBusy(false);
        $ctrl.disableOpenOrderSubmit = false;

        /*------------------------------------------------------------*/
        /* A rejected Alert.confirm (user chose "No" or pressed Esc)  */
        /* rejects with a SweetAlert dismiss string, not a service    */
        /* response object, and lands in this same catch. Treat that  */
        /* as a cancel: the button is re-enabled above, so just exit  */
        /* without surfacing a misleading processing error.           */
        /*------------------------------------------------------------*/
        if (typeof resp === 'string') return;

        Alert.error(
          `An error occurred while attempting to process those open orders. Error: ${Tools.serviceErrorHandler(
            resp
          )}`,
          'Open Orders'
        );
      });
  };
}

/*******************************************************************************
 * refreshOpenOrders */
/**
 * Reloads the open orders list for the selected entity or receipt barcode.
 *******************************************************************************/
/* @ngInject */ function refreshOpenOrders($ctrl, Alert, BusyScreen) {
  return function () {
    BusyScreen.setBusy(true);
    $ctrl.disableOpenOrderSubmit = true;

    return $ctrl
      .viewOpenOrdersHandler()
      .then((openOrders) => {
        $ctrl.dgOpenOrders.data = openOrders;
        Alert.success(
          'Successfully refreshed the list of Open Orders.',
          'Open Orders'
        );
      })
      .finally(() => {
        BusyScreen.setBusy(false);
        $ctrl.disableOpenOrderSubmit = false;
      });
  };
}

/*******************************************************************************
 * refreshReceivables */
/**
 * Reloads the unpaid items grid for the selected entity, clearing the current
 * row selection first.
 *******************************************************************************/
/* @ngInject */ function refreshReceivables($ctrl, Alert, BusyScreen) {
  return function () {
    BusyScreen.setBusy(true);
    // clear selected rows now that we are refreshing the list
    $ctrl.dgReceivables.api.selection.clearSelectedRows();

    return $ctrl
      .viewReceivablesItemsHandler()
      .then((receivables) => {
        $ctrl.dgReceivables.data = receivables;
        Alert.success(
          'Successfully refreshed the list of Unpaid Items.',
          'Unpaid Items'
        );
      })
      .finally(() => {
        BusyScreen.setBusy(false);
        $ctrl.disableOpenOrderSubmit = false;
      });
  };
}

/*******************************************************************************
 * reloadAfterDestinyRefresh */
/**
 * After any manual Destiny refresh attempt, reload Unpaid Items from our
 * database, which also refreshes the Destiny status pills
 * (viewReceivablesItemsHandler updates the dialog's destinyStatus). This
 * happens whatever the outcome: completed, failed, still running, not
 * started (cooldown or another refresh already running), or the request
 * itself failed. Another refresh, or this one finishing after the browser
 * gave up, may have changed the cached fines, so the grid is never left
 * showing what it had before the click.
 *
 * viewReceivablesItemsHandler alerts on a failed reload and resolves with
 * undefined rather than rejecting, so only replace the rows (and clear the
 * selection that pointed at them) when it actually returned a list - a failed
 * read must not blank a grid whose cached data is still intact.
 *
 * @param  $ctrl  Dialog controller.
 *******************************************************************************/
function reloadAfterDestinyRefresh($ctrl) {
  if (!$ctrl.dgReceivables) return;

  return $ctrl.viewReceivablesItemsHandler().then((receivables) => {
    if (!Array.isArray(receivables)) return;

    if ($ctrl.dgReceivables.api) {
      $ctrl.dgReceivables.api.selection.clearSelectedRows();
    }

    $ctrl.dgReceivables.data = receivables;
  });
}

/*******************************************************************************
 * refreshDestinyFines */
/**
 * Runs a manual Destiny fines refresh (the "Refresh Site" link on the Destiny
 * accordion). The server waits up to 30 seconds for it to finish -
 * refreshDestinyFinesHandler's own Alert reports the real outcome, or that it's
 * still running - so the busy screen stays up meanwhile.
 *******************************************************************************/
/* @ngInject */ function refreshDestinyFines($ctrl, BusyScreen) {
  return function () {
    BusyScreen.setBusy(true);

    return $ctrl
      .refreshDestinyFinesHandler()
      .then(() => reloadAfterDestinyRefresh($ctrl))
      .finally(() => {
        BusyScreen.setBusy(false);
      });
  };
}

/*******************************************************************************
 * refreshDestinyEntityFines */
/**
 * Same as refreshDestinyFines above, but scoped to just the currently selected
 * entity (the per-entity "Refresh <student>" link on the Destiny accordion),
 * rather than the whole site.
 *******************************************************************************/
/* @ngInject */ function refreshDestinyEntityFines($ctrl, BusyScreen) {
  return function () {
    BusyScreen.setBusy(true);

    return $ctrl
      .refreshDestinyEntityFinesHandler()
      .then(() => reloadAfterDestinyRefresh($ctrl))
      .finally(() => {
        BusyScreen.setBusy(false);
      });
  };
}

/*******************************************************************************
 * setupOpenOrdersGrid */
/**
 * Builds the open orders grid options.
 *******************************************************************************/
/* @ngInject */ function setupOpenOrdersGrid($ctrl, uiGridConstants) {
  return function () {
    $ctrl.dgOpenOrders = {
      enableColumnMenus: false,
      enableCellEdit: false,
      enableCellEditOnFocus: true,
      enableColumnResizing: true,
      enableFiltering: !$ctrl.addressEntity && !$ctrl.batchNumber,
      enableRowHeaderSelection: false,
      isRowSelectable: () => false,
      enableSorting: true,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      minRowsToShow: 5,
      maxRowsToShow: 5,
      data: $ctrl.openOrders,
      columnDefs: [
        {
          field: 'receiptNumber',
          displayName: 'Rcpt #',
          minWidth: 90,
          type: 'number',
          cellTooltip: true
        },
        {
          field: 'entityName',
          displayName: 'Customer',
          minWidth: 90,
          cellTooltip: true
        },
        {
          field: 'itemName',
          displayName: 'Product',
          minWidth: 300,
          cellTooltip: true
        },
        {
          field: 'qtyOrdered',
          displayName: 'Ordered',
          minWidth: 90,
          type: 'number',
          enableColumnResizing: false
        },
        {
          field: 'qtyDelivered',
          displayName: 'Delivered',
          minWidth: 90,
          type: 'number',
          enableColumnResizing: false
        },
        {
          field: 'qtyRemaining',
          displayName: 'Remaining',
          minWidth: 90,
          type: 'number',
          enableColumnResizing: false
        },
        {
          field: 'qtyFill',
          displayName: 'Deliver',
          minWidth: 90,
          type: 'number',
          enableCellEdit: true,
          type: 'number',
          min: '0',
          enableColumnResizing: false
        }
      ],
      onRegisterApi: function (gridApi) {
        $ctrl.dgOpenOrders.api = gridApi;

        /*----------------------------------------------------*/
        /* Restricts the minimum number of the Deliver option */
        /* to 0 and the max number to the quantity remaining. */
        /*----------------------------------------------------*/
        $ctrl.dgOpenOrders.api.edit.on.afterCellEdit(
          null,
          function (rowEntity, colDef, newVal) {
            rowEntity.qtyFill = Math.max(
              Math.min(newVal, rowEntity.qtyRemaining),
              0
            );
          }
        );
      }
    };
  };
}

/*******************************************************************************
 * setupReceivablesGrid */
/**
 * Builds the unpaid items (receivables) grid options.
 *******************************************************************************/
/* @ngInject */ function setupReceivablesGrid(
  $ctrl,
  uiGridConstants,
  CommonUIGridTemplates,
  Alert
) {
  return function () {
    const creditMemoBtnTemplate = `
      <div class="ui-grid-cell-contents">
        <span 
          class="glyphicon glyphicon-edit csm-grid-space-target credit-memo-btn" 
          ng-click="grid.appScope.$ctrl.creditMemo(row.entity)"
        ></span
      ></div>
    `;

    $ctrl.dgReceivables = {
      enableColumnMenus: false,
      enableCellEditOnFocus: true,
      enableFullRowSelection: false,
      enableRowHeaderSelection: $ctrl.allowReceivablesEdit,
      enableRowSelection: false,
      enableColumnResizing: true,
      enableSorting: true,
      isRowSelectable: function () {
        return $ctrl.allowReceivablesEdit;
      },
      multiSelect: $ctrl.allowReceivablesEdit,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      minRowsToShow: 5,
      maxRowsToShow: 5,
      data: $ctrl.receivables,
      columnDefs: [
        {
          field: 'date',
          displayName: 'Date',
          minWidth: 90,
          cellFilter: "date: 'MM/dd/yyyy'",
          type: 'date',
          enableCellEdit: false
        },
        {
          field: 'subType',
          labelFunction: $ctrl.typeLabelFunction,
          displayName: 'Type',
          minWidth: 90,
          enableCellEdit: false,
          cellTooltip: true
        },
        {
          field: 'receiptNumber',
          displayName: 'Receipt #',
          minWidth: 90,
          type: 'number',
          enableCellEdit: false,
          cellTooltip: true
        },
        {
          field: 'description',
          displayName: 'Description',
          enableCellEdit: false,
          cellTooltip: true
        },
        {
          field: 'balance',
          displayName: 'Balance Due',
          minWidth: 120,
          cellFilter: 'currency',
          type: 'number',
          enableCellEdit: false
        },
        {
          field: 'amountToPay',
          displayName: 'Amount to Pay',
          cellFilter: 'currency',
          type: 'number',
          enableCellEdit: true,
          editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
          precision: 2,
          allowNegative: false
        },
        {
          field: 'credit',
          displayName: 'CR Memo',
          minWidth: 90,
          enableSorting: false,
          enableCellEdit: false,
          cellTemplate: creditMemoBtnTemplate,
          enableColumnResizing: false
        }
      ],
      onRegisterApi: function (gridApi) {
        $ctrl.dgReceivables.api = gridApi;
        gridApi.edit.on.afterCellEdit(null, (receivable) => {
          if (receivable.amountToPay > receivable.balance) {
            Alert.info(
              `
              You can not pay more than the balance ($${receivable.balance.toFixed(2)}) of the Unpaid Item. 
              Resetting the Amount to Pay to $${receivable.balance.toFixed(2)}
            `,
              'Unpaid Items'
            );

            receivable.amountToPay = receivable.balance;
          } else if (receivable.amountToPay === 0) {
            Alert.info(
              `The Amount to Pay for the Unpaid Item can not be $0. Resetting the Amount to Pay to $${receivable.balance.toFixed(2)}`,
              'Unpaid Items'
            );

            receivable.amountToPay = receivable.balance;
          } else if (receivable.amountToPay !== receivable.balance) {
            /**
             * If the user changes the amount to pay to be different than the balance then we need to select the row because
             * the user is indicating that they want to pay this item. We also do not need to worry about amountToPay being negative because
             * our input field does not allow negative numbers.
             */
            $ctrl.dgReceivables.api.selection.toggleRowSelection(receivable);
          }
        });
      }
    };

    /**
     * In the case that we are showing receivables for a receipt or org we add the entity name as
     * a column in the grid. We have to do this otherwise the users could not tell who the unpaid item is for.
     */
    if (!$ctrl.addressEntity) {
      $ctrl.dgReceivables.columnDefs.splice(1, 0, {
        field: 'entityName',
        displayName: 'For',
        minWidth: 90,
        cellTooltip: true,
        enableCellEdit: false
      });
    }
  };
}

/*******************************************************************************
 * showDestiny */
/**
 * Whether to show the Destiny accordion (freshness status and the "Refresh
 * Site"/"Refresh <student>" links). Requires both:
 *
 * - hasDestiny The server says this school can take Destiny payments (see
 * DestinyDBA.isReadyForPayments). - student The selected entity is a student -
 * its types include ET_STUDENT (same check as IndividualInfo's entityIsAStudent)
 * AND it has a StudentID. The type is checked too so a non-student that somehow
 * has a StudentID populated doesn't qualify. Destiny fines are only ever tracked
 * for students (looked up by StudentID), so none of it applies to anyone else -
 * or when no entity is selected at all (receipt/org-wide lookups never load
 * Destiny fines). The server also rejects a per-entity refresh for a non-student
 * (startManualEntityRefresh).
 *******************************************************************************/
/* @ngInject */ function showDestiny($ctrl, SERVER) {
  return function () {
    const entity = $ctrl.addressEntity?.entity;
    const isStudent =
      !!entity?.types?.some(
        (type) => type === SERVER.BooksDBConstants.ET_STUDENT
      ) && !!entity.studentID;

    return !!$ctrl.destinyStatus?.hasDestiny && isStudent;
  };
}

/*******************************************************************************
 * typeLabelFunction */
/**
 * Formats the data shown in the type column in the receivables grid. This has to
 * be done before the grids are created.
 *
 * @param  rowEntity  grid row
 *******************************************************************************/
/* @ngInject */ function typeLabelFunction(SERVER) {
  return function (rowEntity) {
    return rowEntity.subType ===
      SERVER.BooksDBConstants.LIS_RECEIVABLE_ITEM_CHARGE
      ? 'ITEM'
      : rowEntity.subType === SERVER.BooksDBConstants.LIS_FINE
        ? 'FINE'
        : 'UNKNOWN';
  };
}
